import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  message,
  Select,
  Space,
  Typography,
} from 'antd';
import Logo from '../../Logo';
import { UserContext } from '../../UserContext';

const { Title, Text, Paragraph } = Typography;

// Client-206 mock lead (FUB test person) used for Lead Engagement test calls (HOU-3793),
// so testers only need to enter a number to call.
const DEFAULT_SOURCE_LEAD_ID = '112';
// The dev ElevenLabs agent answers test calls (the backend defaults to this same agent).
const DEFAULT_ELEVEN_LABS_AGENT_ID = 'agent_2001kmk9tv8tf6bak5x5vdwshqb1';
// Client-206 FUB (staging) person-link template — mirrors the client's crm_link config.
// Hardcoded because this is an internal temp test page pinned to client 206.
const FUB_PERSON_LINK_TEMPLATE =
  'https://fub-staging.followupboss.com/2/people/view/{lead_source_id}';
// Client-206 mock Kelley Blue Book Homes seller, with the campaign's day-1 call opening.
// The figures are lead 304's own report, so they're dropped for any other lead.
const KBB_SOURCE_LEAD_ID = '304';
const KBB_REACHOUT_LINE =
  'I have your Kelley Blue Book Homes report for your place.';
const KBB_REPORT_FIGURES =
  ' It came in around $688K, and we think with our experts you could get up to $755K. ' +
  'Want me to walk you through what is behind the number?';

const kbbReachoutLine = (sourceLeadId: string): string =>
  sourceLeadId === KBB_SOURCE_LEAD_ID
    ? KBB_REACHOUT_LINE + KBB_REPORT_FIGURES
    : KBB_REACHOUT_LINE;

interface TestCallForm {
  numberToCall: string;
  sourceLeadId: string;
  campaignKey: string;
  elevenLabsAgentId: string;
  machineDetection: boolean;
  campaignReachoutLine: string;
}

interface TestCallResult {
  lead_id: string;
  lead_call_id: string;
  eleven_labs_agent_id: string;
}

interface TestCallCampaign {
  key: string;
  name: string;
}

export const LeadEngagementTestPage = ({
  apiUrl,
}: {
  apiUrl: string;
}): JSX.Element => {
  const user = useContext(UserContext);
  const [form] = Form.useForm<TestCallForm>();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TestCallResult | undefined>(undefined);
  const [campaigns, setCampaigns] = useState<TestCallCampaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const sourceLeadId = Form.useWatch('sourceLeadId', form);
  const fubLink = sourceLeadId
    ? FUB_PERSON_LINK_TEMPLATE.replace('{lead_source_id}', sourceLeadId)
    : '';

  useEffect(() => {
    const controller = new AbortController();
    const fetchCampaigns = async () => {
      setCampaignsLoading(true);
      try {
        const response = await fetch(`${apiUrl}/api/speed-to-lead/campaigns`, {
          headers: user?.accessToken
            ? { Authorization: `Bearer ${user.accessToken}` }
            : {},
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(
            `Failed to load campaigns: ${response.status} ${await response.text()}`,
          );
        }
        const data: { campaigns: TestCallCampaign[] } = await response.json();
        if (!controller.signal.aborted) {
          setCampaigns(data.campaigns);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          void message.error(`${err}`);
        }
      } finally {
        if (!controller.signal.aborted) {
          setCampaignsLoading(false);
        }
      }
    };
    void fetchCampaigns();
    return () => controller.abort();
  }, [apiUrl, user?.accessToken]);

  const handleSubmit = async (values: TestCallForm) => {
    setLoading(true);
    setResult(undefined);
    try {
      const response = await fetch(
        `${apiUrl}/api/speed-to-lead/start/${encodeURIComponent(values.sourceLeadId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user?.accessToken
              ? { Authorization: `Bearer ${user.accessToken}` }
              : {}),
          },
          body: JSON.stringify({
            number_to_call: values.numberToCall,
            campaign_key: values.campaignKey || undefined,
            eleven_labs_agent_id: values.elevenLabsAgentId || undefined,
            machine_detection: values.machineDetection,
            campaign_reachout_line: values.campaignReachoutLine || undefined,
          }),
        },
      );
      if (!response.ok) {
        throw new Error(
          `Failed to start test call: ${response.status} ${await response.text()}`,
        );
      }
      const data: TestCallResult = await response.json();
      setResult(data);
      void message.success(
        'Test call started — your phone should ring shortly.',
      );
    } catch (err) {
      void message.error(`${err}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        padding: 24,
        maxWidth: 520,
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      <Logo />
      <Title level={3}>Lead Engagement Test Call</Title>
      <Paragraph type="secondary">
        Places an outbound Lead Engagement call (client 206) answered by the dev
        voice agent. Enter the number to call — the other fields are prefilled
        defaults.
      </Paragraph>
      <Divider />
      <Form
        form={form}
        initialValues={{
          sourceLeadId: DEFAULT_SOURCE_LEAD_ID,
          campaignKey: '',
          elevenLabsAgentId: DEFAULT_ELEVEN_LABS_AGENT_ID,
          machineDetection: true,
        }}
        layout="vertical"
        onFinish={values => void handleSubmit(values)}
        onValuesChange={(changed: Partial<TestCallForm>) => {
          if (
            changed.sourceLeadId !== undefined &&
            form
              .getFieldValue('campaignReachoutLine')
              ?.startsWith(KBB_REACHOUT_LINE)
          ) {
            form.setFieldValue(
              'campaignReachoutLine',
              kbbReachoutLine(changed.sourceLeadId),
            );
          }
        }}
      >
        <Form.Item
          label="Number to call"
          name="numberToCall"
          rules={[
            {
              required: true,
              message:
                'Enter the phone number to call (E.164, e.g. +12065551234)',
            },
          ]}
        >
          <Input placeholder="+12065551234" />
        </Form.Item>
        <Form.Item
          extra={
            fubLink ? (
              <a href={fubLink} rel="noopener noreferrer" target="_blank">
                View lead in Follow Up Boss
              </a>
            ) : undefined
          }
          label="Mock lead source id (client 206)"
          name="sourceLeadId"
          rules={[
            { required: true, message: 'A mock lead source id is required' },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          extra="Starting a call saves the selected campaign on this test lead for future calls."
          label="Campaign"
          name="campaignKey"
        >
          <Select
            disabled={campaignsLoading}
            loading={campaignsLoading}
            optionFilterProp="label"
            options={[
              { label: "Use lead's saved campaign", value: '' },
              ...campaigns.map(campaign => ({
                label: campaign.name,
                value: campaign.key,
              })),
            ]}
            showSearch
          />
        </Form.Item>
        <Form.Item label="ElevenLabs agent id (dev)" name="elevenLabsAgentId">
          <Input />
        </Form.Item>
        <Form.Item
          extra={
            <Button
              onClick={() =>
                form.setFieldsValue({
                  sourceLeadId: KBB_SOURCE_LEAD_ID,
                  campaignKey: 'kbb_hot_seller',
                  campaignReachoutLine: kbbReachoutLine(KBB_SOURCE_LEAD_ID),
                })
              }
              style={{ padding: 0 }}
              type="link"
            >
              Use KBB seller lead
            </Button>
          }
          label="Campaign reachout line (optional)"
          name="campaignReachoutLine"
        >
          <Input.TextArea autoSize={{ minRows: 2 }} />
        </Form.Item>
        <Form.Item name="machineDetection" valuePropName="checked">
          <Checkbox>Twilio voicemail detection</Checkbox>
        </Form.Item>
        <Form.Item>
          <Button htmlType="submit" loading={loading} type="primary">
            Start test call
          </Button>
        </Form.Item>
      </Form>
      {result && (
        <Space direction="vertical">
          <Text strong>Call started</Text>
          <Text>lead_call_id: {result.lead_call_id}</Text>
          <Text>agent: {result.eleven_labs_agent_id}</Text>
        </Space>
      )}
    </div>
  );
};
